import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/lib/i18n";
import { api, productThumb } from "@/lib/api";
import { ArrowRight, SlidersHorizontal, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SEO from "@/components/SEO";
import { RecentlyViewed } from "@/components/RecentlyViewed";

export default function SpaSeriesPage() {
  const { t, tp } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [filters, setFilters] = useState({ capacity: "", min_jets: "", installation_type: "" });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    const params = { series: "spa" };
    if (filters.capacity) params.capacity = filters.capacity;
    if (filters.min_jets) params.min_jets = filters.min_jets;
    if (filters.installation_type) params.installation_type = filters.installation_type;
    api.getProducts(params).then(setProducts).catch(() => setLoadError(true)).finally(() => setLoading(false));
  }, [filters, retryKey]);

  const clearFilters = () => setFilters({ capacity: "", min_jets: "", installation_type: "" });
  const hasFilters = Object.values(filters).some(Boolean);

  // FAQ data for schema and display
  const faqData = [
    {
      question: t("spaSeriesPage.faq1Q"),
      answer: t("spaSeriesPage.faq1A")
    },
    {
      question: t("spaSeriesPage.faq2Q"),
      answer: t("spaSeriesPage.faq2A")
    },
    {
      question: t("spaSeriesPage.faq3Q"),
      answer: t("spaSeriesPage.faq3A")
    },
    {
      question: t("spaSeriesPage.faq4Q"),
      answer: t("spaSeriesPage.faq4A")
    }
  ];

  // Generate itemList for schema when products are loaded
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : "https://www.pruvajacuzzi.com";
  const itemListData = products.length > 0 ? {
    name: "Pruva Spa Series Collection",
    description: "Large hydrotherapy spa systems for villas, rooftops, and hospitality in Dubai, UAE",
    items: products.map(p => ({
      name: p.name,
      url: `${baseUrl}/spa-series/${p.slug}`,
      image: p.image_url
    }))
  } : null;

  return (
    <div data-testid="spa-series-page">
      <SEO 
        title="Spa Series - Large Hydrotherapy Spas for Dubai Villas"
        description="Explore Pruva's Spa Series - spacious spa models (4-10+ person) designed for Dubai villas, rooftops, and hospitality. Advanced Balboa controls, up to 116 hydro jets, American Aristech acrylic. Free consultation available."
        keywords="spa dubai, large spa, family spa, hydrotherapy spa, balboa spa, outdoor spa dubai, villa spa, rooftop spa installation, luxury spa UAE, spa pool dubai, spa price dubai"
        canonical={`${baseUrl}/spa-series`}
        faq={faqData}
        breadcrumbs={[
          { name: "Home", url: baseUrl },
          { name: "Spa Series", url: `${baseUrl}/spa-series` }
        ]}
        itemList={itemListData}
      />
      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20" style={{ background: "linear-gradient(135deg, #F8F9FA 0%, #F1F3F5 100%)" }}>
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <p className="text-sm uppercase tracking-widest text-amber-700 font-medium mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>
            {t("products.spaSeries")}
          </p>
          <h1 data-testid="spa-series-title" className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 mb-6">
            {t("spaSeriesPage.heroTitle")}
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
            {t("spaSeriesPage.heroDesc1")}
          </p>
          <p className="text-base text-slate-500 max-w-3xl mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
            {t("spaSeriesPage.heroDesc2")}
          </p>
          <p className="text-base text-slate-500 max-w-3xl" style={{ fontFamily: "'Inter', sans-serif" }}>
            {t("spaSeriesPage.heroDesc3")}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <h2 className="text-2xl md:text-3xl font-medium text-slate-900 mb-8">{t("spaSeriesPage.browseCollection")}</h2>
          
          {/* Filter Toggle */}
          <div className="flex items-center justify-between mb-10">
            <p className="text-sm text-slate-500" style={{ fontFamily: "'Inter', sans-serif" }}>
              {loading ? (
                <span data-testid="count-loading" className="inline-block h-4 w-24 bg-slate-200 animate-pulse rounded align-middle" />
              ) : (
                <>{products.length} {t("products.spaSeries").toLowerCase()}</>
              )}
            </p>
            <button data-testid="toggle-filters" onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 text-sm text-slate-800 font-medium hover:text-slate-600 transition-colors duration-200">
              <SlidersHorizontal className="w-4 h-4" /> {t("filters.title")}
            </button>
          </div>

          {/* Filters */}
          {showFilters && (
            <div data-testid="filters-panel" className="mb-10 p-6 bg-slate-50 border border-slate-100 animate-fade-in">
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2 block">{t("filters.capacity")}</label>
                  <Select value={filters.capacity} onValueChange={v => setFilters(f => ({ ...f, capacity: v === "any" ? "" : v }))}>
                    <SelectTrigger data-testid="filter-capacity"><SelectValue placeholder={t("filters.anyCapacity")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">{t("filters.anyCapacity")}</SelectItem>
                      <SelectItem value="2">2+</SelectItem>
                      <SelectItem value="4">4+</SelectItem>
                      <SelectItem value="6">6+</SelectItem>
                      <SelectItem value="10">10+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2 block">{t("filters.jets")}</label>
                  <Select value={filters.min_jets} onValueChange={v => setFilters(f => ({ ...f, min_jets: v === "any" ? "" : v }))}>
                    <SelectTrigger data-testid="filter-jets"><SelectValue placeholder={t("filters.anyJets")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">{t("filters.anyJets")}</SelectItem>
                      <SelectItem value="20">20+</SelectItem>
                      <SelectItem value="40">40+</SelectItem>
                      <SelectItem value="60">60+</SelectItem>
                      <SelectItem value="100">100+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2 block">{t("filters.installation")}</label>
                  <Select value={filters.installation_type} onValueChange={v => setFilters(f => ({ ...f, installation_type: v === "any" ? "" : v }))}>
                    <SelectTrigger data-testid="filter-installation"><SelectValue placeholder={t("filters.anyInstallation")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">{t("filters.anyInstallation")}</SelectItem>
                      <SelectItem value="terrace">{t("filters.terrace")}</SelectItem>
                      <SelectItem value="villa">{t("filters.villa")}</SelectItem>
                      <SelectItem value="rooftop">{t("filters.rooftop")}</SelectItem>
                      <SelectItem value="hospitality">{t("filters.hospitality")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {hasFilters && (
                <button data-testid="clear-filters" onClick={clearFilters}
                  className="mt-4 flex items-center gap-1 text-sm text-slate-800 hover:text-slate-600 font-medium transition-colors duration-200">
                  <X className="w-3.5 h-3.5" /> {t("filters.clearAll")}
                </button>
              )}
            </div>
          )}

          {/* Product Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1,2,3].map(i => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-slate-200" />
                  <div className="p-6 space-y-3"><div className="h-5 bg-slate-200 w-1/2" /><div className="h-4 bg-slate-200 w-3/4" /></div>
                </div>
              ))}
            </div>
          ) : loadError ? (
            <div data-testid="load-error" className="text-center py-16">
              <p className="text-slate-500 mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>{t("products.loadError")}</p>
              <button data-testid="retry-load" onClick={() => setRetryKey(k => k + 1)}
                className="bg-slate-800 text-white px-8 py-3 text-sm uppercase tracking-widest font-medium hover:bg-slate-700 transition-colors duration-300">
                {t("products.tryAgain")}
              </button>
            </div>
          ) : products.length === 0 ? (
            <p data-testid="no-products" className="text-center text-slate-500 py-16" style={{ fontFamily: "'Inter', sans-serif" }}>{t("products.noProducts")}</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map(p => (
                <Link key={p.slug} to={`/spa-series/${p.slug}`} data-testid={`product-card-${p.slug}`}
                  className="group bg-white overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={productThumb(p)} onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = p.image_url; }} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" width="500" height="500" decoding="async" />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-medium text-slate-900 mb-1" style={{ fontFamily: "'Inter', sans-serif" }}>{p.name}</h3>
                    <p className="text-sm text-slate-500 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>{tp(p.subtitle)}</p>
                    <div className="flex flex-wrap gap-3 text-xs text-slate-400" style={{ fontFamily: "'Inter', sans-serif" }}>
                      <span>{p.capacity} {t("products.persons")}</span>
                      <span className="text-slate-200">|</span>
                      <span>{p.hydro_jets} {t("products.hydroJets")}</span>
                      <span className="text-slate-200">|</span>
                      <span dir="ltr">{p.dimensions} mm</span>
                    </div>
                    <span className="inline-flex items-center gap-2 text-sm text-slate-800 mt-4 uppercase tracking-widest font-medium group-hover:gap-3 transition-all duration-300">
                      {t("products.viewDetails")} <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-medium text-slate-900 mb-8 text-center">{t("spaSeriesPage.faqTitle")}</h2>
          <div className="space-y-4">
            {faqData.map((faq, i) => (
              <details key={i} className="bg-white border border-slate-200 p-6 group">
                <summary className="flex justify-between items-center cursor-pointer list-none text-lg font-medium text-slate-900">
                  {faq.question}
                  <span className="text-slate-400 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <p className="mt-4 text-slate-600 leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-Sell Section */}
      <section className="py-12 md:py-16 bg-white border-t border-slate-100">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <h2 className="text-xl md:text-2xl font-medium text-slate-900 mb-6 text-center">{t("spaSeriesPage.exploreMore")}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/jacuzzi-series" className="p-6 bg-slate-50 border border-slate-100 hover:border-slate-300 transition-colors text-center">
              <h3 className="text-lg font-medium text-slate-900 mb-2">{t("spaSeriesPage.jacuzziSeriesLink")}</h3>
              <p className="text-sm text-slate-600">{t("spaSeriesPage.jacuzziSeriesDesc")}</p>
            </Link>
            <Link to="/custom-solutions" className="p-6 bg-slate-50 border border-slate-100 hover:border-slate-300 transition-colors text-center">
              <h3 className="text-lg font-medium text-slate-900 mb-2">{t("nav.customSolutions")}</h3>
              <p className="text-sm text-slate-600">{t("jacuzziSeriesPage.customDesc")}</p>
            </Link>
          </div>
        </div>
      </section>

      <RecentlyViewed />
    </div>
  );
}
